/**
 * The design system's view of a failed Supabase call. A thin adapter over
 * `describeError` in @gymcoach/shared, which holds the rules (shared with the app):
 *
 *   P0001  a refusal written for people (a guard in an RPC)  → shown VERBATIM
 *   42501  not allowed                                         → "You don't have access to this"
 *   P0002  not found (PostgREST 404 / PGRST116 too)            → "Not found"
 *
 * "Not found" is deliberately bare. RLS makes another gym's record and one that does
 * not exist indistinguishable; a hedged "you may not have access" would undo that.
 *
 * `ErrorState` and `Form` use these; screens normally just pass the raw error to them.
 */
import { describeError, type DescribedError } from '@gymcoach/shared';

export type { DescribedError };
export { describeError };

/** The words to show. Refusals verbatim; everything else in plain language. */
export function userMessage(error: unknown): string {
  return describeError(error).message;
}

export function isNotFound(error: unknown): boolean {
  return describeError(error).kind === 'not_found';
}

export function isForbidden(error: unknown): boolean {
  return describeError(error).kind === 'forbidden';
}

/** A refusal the person can act on (P0001): show it, do not offer "Try again". */
export function isRefusal(error: unknown): boolean {
  return describeError(error).kind === 'refusal';
}

/** Whether retrying could help (network blips, unknown failures). */
export function isRetryable(error: unknown): boolean {
  return describeError(error).retryable;
}

/** Field-level messages (from an Edge Function), keyed by form field name. */
export function fieldErrors(error: unknown): Record<string, string> {
  return error ? describeError(error).fieldErrors : {};
}

/** A heading for an error panel. */
export function errorTitle(error: unknown): string {
  switch (describeError(error).kind) {
    case 'not_found':
      return 'Not found';
    case 'forbidden':
      return 'Not allowed';
    case 'refusal':
      return "That couldn't be done";
    case 'network':
      return 'No connection';
    case 'unauthenticated':
      return 'Signed out';
    default:
      return 'Something went wrong';
  }
}
